import React, { useState } from "react";
import { Table, Tag, Dropdown, MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { Interview } from "../../../../core/data/redux/recruitment/types";
import ApplicationPagination from "../../applications/components/ApplicationPagination";

interface InterviewTableProps {
  data: Interview[];
  loading: boolean;
  onViewFeedback: (interviewId: number) => void;
  onReschedule: (interview: Interview) => void;
  onCancel: (interviewId: number) => void;
  onComplete?: (interviewId: number) => void;
  onStatusChange?: (interviewId: number, newStatus: string) => void;
}

const statusConfig: Record<string, { color: string; icon: string }> = {
  scheduled: { color: "blue", icon: "ti-calendar-event" },
  completed: { color: "green", icon: "ti-check" },
  cancelled: { color: "red", icon: "ti-x" },
  rescheduled: { color: "orange", icon: "ti-clock" },
  no_show: { color: "purple", icon: "ti-user-off" },
  passed: { color: "green", icon: "ti-check" },
  failed: { color: "red", icon: "ti-x" },
};

const InterviewTable: React.FC<InterviewTableProps> = ({
  data,
  loading,
  onViewFeedback,
  onReschedule,
  onCancel,
  onComplete,
  onStatusChange,
}) => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const totalCount = data.length;
  const startIdx = (page - 1) * limit;
  const endIdx = startIdx + limit;
  const paginatedData = data.slice(startIdx, endIdx);

  const getActionItems = (record: Interview): MenuProps["items"] => {
    const items: MenuProps["items"] = [];

    if (record.meeting_link) {
      items.push({
        key: "join",
        icon: <i className="ti ti-video" style={{ color: "#10b981" }} />,
        label: (
          <a
            href={record.meeting_link}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}>
            Join Meeting
          </a>
        ),
      });
      items.push({ type: "divider" });
    }

    if (record.status === "completed" && !record.feedback_submitted) {
      items.push({
        key: "feedback",
        icon: <i className="ti ti-message" style={{ color: "#3b82f6" }} />,
        label: "Submit Feedback",
        onClick: () => onViewFeedback(record.interview_id),
      });
    }

    if (record.status === "scheduled" || record.status === "rescheduled") {
      // Mark as Completed
      items.push({
        key: "complete",
        icon: <i className="ti ti-circle-check" style={{ color: "#10b981" }} />,
        label: "Mark as Completed",
        onClick: () => onComplete?.(record.interview_id),
      });

      // Mark as No Show
      items.push({
        key: "no_show",
        icon: <i className="ti ti-user-off" style={{ color: "#7c3aed" }} />,
        label: "Mark as No Show",
        onClick: () => onStatusChange?.(record.interview_id, "no_show"),
      });

      items.push({ type: "divider" });

      items.push({
        key: "reschedule",
        icon: <i className="ti ti-edit" style={{ color: "#f59e0b" }} />,
        label: "Edit Interview",
        onClick: () => onReschedule(record),
      });

      items.push({ type: "divider" });

      items.push({
        key: "cancel",
        icon: <i className="ti ti-x" />,
        danger: true,
        label: "Cancel Interview",
        onClick: () => onCancel(record.interview_id),
      });
    }

    // Allow rescheduling no-shows
    if (record.status === "no_show") {
      items.push({
        key: "reschedule_noshow",
        icon: <i className="ti ti-edit" style={{ color: "#f59e0b" }} />,
        label: "Edit Interview",
        onClick: () => onReschedule(record),
      });
    }

    // Clean trailing dividers
    if (items.length > 0) {
      const lastItem = items[items.length - 1];
      if (lastItem && "type" in lastItem && lastItem.type === "divider") {
        items.pop();
      }
    }

    return items;
  };

  const columns: ColumnsType<Interview> = [
    {
      title: "Sr.",
      key: "srNo",
      width: 55,
      align: "center",
      render: (_, __, index) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {(page - 1) * limit + index + 1}
        </span>
      ),
    },
    {
      title: "Candidate",
      key: "candidate",
      width: 220,
      sorter: (a, b) =>
        (a.candidate?.full_name || "").localeCompare(
          b.candidate?.full_name || "",
        ),
      render: (_, record) => (
        <div>
          <h6 className="fw-medium fs-14 mb-0">
            {record.candidate?.full_name || record.candidate_name}
          </h6>
          <span className="text-muted fs-12">
            {record.job?.job_title || record.job_title}
          </span>
        </div>
      ),
    },
    {
      title: "Round",
      key: "round",
      width: 180,
      render: (_, record) => (
        <div>
          <div className="fw-medium fs-13">{record.round_name}</div>
          <span className="text-muted fs-12">
            {record.round_type?.replace(/_/g, " ")}
          </span>
        </div>
      ),
    },
    {
      title: "Date & Time",
      key: "datetime",
      width: 200,
      sorter: (a, b) =>
        new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime(),
      render: (_, record) => {
        const dt = new Date(record.scheduled_at);
        return (
          <div>
            <div className="fs-13">
              <i className="ti ti-calendar me-1 text-muted" />
              {dt.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
            <div className="text-muted fs-12">
              <i className="ti ti-clock me-1" />
              {dt.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}{" "}
              ({record.duration_minutes} min)
            </div>
          </div>
        );
      },
    },
    {
      title: "Mode",
      key: "mode",
      width: 130,
      render: (_, record) => {
        const mode = record.interview_mode || record.round_type;
        return <Tag color="cyan">{mode?.replace(/_/g, " ")}</Tag>;
      },
    },
    {
      title: "Interviewers",
      key: "interviewers",
      width: 140,
      render: (_, record: any) => {
        const count =
          record.interviewers?.length || record.interviewer_ids?.length || 0;
        if (count === 0) return <span className="text-muted fs-12">—</span>;
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: 12,
              color: "#475569",
            }}>
            <i
              className="ti ti-users"
              style={{ fontSize: 14, color: "#6366f1" }}
            />
            {count} assigned
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      render: (status: string) => {
        const cfg = statusConfig[status] || {
          color: "default",
          icon: "ti-point",
        };
        return (
          <Tag color={cfg.color}>
            <i className={`ti ${cfg.icon} me-1`} />
            {status
              ?.replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase())}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center",
      render: (_, record) => {
        const items = getActionItems(record) || [];
        if (items.length === 0) return null;
        return (
          <Dropdown
            menu={{ items }}
            trigger={["click"]}
            placement="bottomRight">
            <button
              className="btn btn-icon btn-sm btn-light"
              style={{ borderRadius: "6px" }}
              onClick={(e) => e.stopPropagation()}>
              <i className="ti ti-dots-vertical" />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="card-body p-0 mb-3">
      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{ minHeight: 400, borderRadius: 12 }}>
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading interviews...</p>
          </div>
        </div>
      ) : data.length > 0 ? (
        <div
          className="bg-white"
          style={{
            borderRadius: 12,
            overflow: "hidden",
            border: "1px solid #e2e8f0",
          }}>
          <Table
            className="custom-data-table"
            columns={columns}
            dataSource={paginatedData}
            rowKey="interview_id"
            size="middle"
            pagination={false}
            scroll={{ x: 1200 }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "data-table-row-light" : "data-table-row-dark"
            }
          />
          <ApplicationPagination
            currentPage={page}
            totalPages={Math.ceil(totalCount / limit)}
            total={totalCount}
            limit={limit}
            hasNextPage={endIdx < totalCount}
            hasPrevPage={page > 1}
            onPageChange={(p) => setPage(p)}
            onLimitChange={(l) => {
              setLimit(l);
              setPage(1);
            }}
          />
        </div>
      ) : (
        <div
          className="d-flex justify-content-center align-items-center bg-white"
          style={{
            minHeight: 400,
            borderRadius: 12,
            border: "1px solid #e2e8f0",
          }}>
          <div className="text-center">
            <div className="mb-3">
              <i
                className="ti ti-calendar-off text-muted"
                style={{ fontSize: "4rem" }}
              />
            </div>
            <h5 className="mt-3 mb-2 fw-semibold">No Interviews Found</h5>
            <p className="text-muted mb-0">
              No interviews match the current view
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewTable;
